import {
  emptyJobRoleContent,
  normalizeJobRoleContent,
  type ClassificationStatus,
  type JobRoleContent,
  type JobRoleHeaderInput,
} from './types';

/**
 * The editable shape of a role (header + content document) as plain strings, so a textarea keeps exactly what the person typed
 * (trailing newlines included). A list field is one item per line. `inputFromForm` turns it back into the wire shape.
 *
 * Every key here has an entry in guidance/roleFieldRegistry.ts (a test enforces it), so the screen always has its definition, why we ask,
 * example and requirement for every field the person can touch.
 */

export const TEXT_KEYS = [
  'title', 'description', 'roleCode', 'departmentId', 'jobFamily', 'careerLevel', 'managementLevel', 'socCode', 'onetCode',
  'socStatus', 'socNotYetClassifiedReason', 'onetStatus', 'onetNotYetClassifiedReason',
  'purpose', 'summary', 'decisionAuthority', 'budgetAuthority', 'supervisoryResponsibilities', 'internalRelationships', 'externalRelationships', 'reportsTo',
  'minimumEducation', 'preferredEducation', 'minimumExperience', 'preferredExperience', 'travel', 'workArrangement',
  'physicalRequirements', 'workingConditions',
  'typicalEmploymentType', 'typicalPayType', 'hourlyOrSalaried', 'flsaClassification', 'commissionEligible', 'bonusEligible', 'overtimeEligible',
  'benefitEligibilityGuidance',
] as const;

export const LIST_KEYS = [
  'alternateTitles', 'essentialDuties', 'responsibilities', 'expectedOutcomes', 'complianceResponsibilities', 'performanceIndicators',
  'licenses', 'certifications', 'technicalSkills', 'professionalSkills', 'preferredSkills', 'languages',
] as const;

export type RoleFormKey = (typeof TEXT_KEYS)[number] | (typeof LIST_KEYS)[number];
export type RoleForm = Record<RoleFormKey, string>;

const listKeySet = new Set<string>(LIST_KEYS);
export const isListKey = (key: string): boolean => listKeySet.has(key);

const lines = (items: string[]) => items.join('\n');
/** One item per non-blank line, trimmed. */
export const splitLines = (text: string): string[] => text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
const split = splitLines;
const text = (value: string | null | undefined) => value ?? '';
const nullIfBlank = (value: string) => (value.trim() ? value.trim() : null);
const tri = (value: boolean | null | undefined) => (value === true ? 'yes' : value === false ? 'no' : '');
const fromTri = (value: string) => (value === 'yes' ? true : value === 'no' ? false : null);
const statusFromForm = (value: string): ClassificationStatus | null => (value === 'Confirmed' || value === 'NotYetClassified' ? value : null);

export interface RoleHeaderLike {
  title?: string | null;
  description?: string | null;
  roleCode?: string | null;
  departmentId?: string | null;
  jobFamily?: string | null;
  careerLevel?: string | null;
  managementLevel?: string | null;
  socCode?: string | null;
  onetCode?: string | null;
}

export function formFromRole(header: RoleHeaderLike, rawContent: Partial<JobRoleContent> | null | undefined): RoleForm {
  const c = normalizeJobRoleContent(rawContent);
  const q = c.qualifications;
  const g = c.employmentGuidance;
  const k = c.classification;
  return {
    title: text(header.title), description: text(header.description), roleCode: text(header.roleCode), departmentId: text(header.departmentId), jobFamily: text(header.jobFamily),
    careerLevel: text(header.careerLevel), managementLevel: text(header.managementLevel), socCode: text(header.socCode), onetCode: text(header.onetCode),
    socStatus: text(k.socStatus), socNotYetClassifiedReason: text(k.socNotYetClassifiedReason), onetStatus: text(k.onetStatus),
    onetNotYetClassifiedReason: text(k.onetNotYetClassifiedReason),
    purpose: text(c.purpose), summary: text(c.summary), alternateTitles: lines(c.alternateTitles), essentialDuties: lines(c.essentialDuties),
    responsibilities: lines(c.responsibilities), expectedOutcomes: lines(c.expectedOutcomes), decisionAuthority: text(c.decisionAuthority),
    budgetAuthority: text(c.budgetAuthority), complianceResponsibilities: lines(c.complianceResponsibilities), performanceIndicators: lines(c.performanceIndicators),
    supervisoryResponsibilities: text(c.supervisoryResponsibilities), internalRelationships: text(c.internalRelationships),
    externalRelationships: text(c.externalRelationships), reportsTo: text(c.reportsTo),
    minimumEducation: text(q.minimumEducation), preferredEducation: text(q.preferredEducation), minimumExperience: text(q.minimumExperience),
    preferredExperience: text(q.preferredExperience), licenses: lines(q.licenses), certifications: lines(q.certifications),
    technicalSkills: lines(q.technicalSkills), professionalSkills: lines(q.professionalSkills), preferredSkills: lines(q.preferredSkills),
    languages: lines(q.languages), travel: text(q.travel), workArrangement: text(q.workArrangement), physicalRequirements: text(q.physicalRequirements),
    workingConditions: text(q.workingConditions),
    typicalEmploymentType: text(g.typicalEmploymentType),
    typicalPayType: text(g.typicalPayType), hourlyOrSalaried: text(g.hourlyOrSalaried), flsaClassification: text(g.flsaClassification),
    commissionEligible: tri(g.commissionEligible), bonusEligible: tri(g.bonusEligible), overtimeEligible: tri(g.overtimeEligible),
    benefitEligibilityGuidance: text(g.benefitEligibilityGuidance),
  };
}

export function emptyForm(): RoleForm {
  return formFromRole({}, emptyJobRoleContent());
}

export function contentFromForm(f: RoleForm, base?: JobRoleContent): JobRoleContent {
  const b = base ?? emptyJobRoleContent();
  const socStatus = statusFromForm(f.socStatus);
  const onetStatus = statusFromForm(f.onetStatus);
  return {
    ...b,
    purpose: nullIfBlank(f.purpose), summary: nullIfBlank(f.summary), alternateTitles: split(f.alternateTitles), essentialDuties: split(f.essentialDuties),
    responsibilities: split(f.responsibilities), expectedOutcomes: split(f.expectedOutcomes), decisionAuthority: nullIfBlank(f.decisionAuthority),
    budgetAuthority: nullIfBlank(f.budgetAuthority), complianceResponsibilities: split(f.complianceResponsibilities), performanceIndicators: split(f.performanceIndicators),
    supervisoryResponsibilities: nullIfBlank(f.supervisoryResponsibilities), internalRelationships: nullIfBlank(f.internalRelationships),
    externalRelationships: nullIfBlank(f.externalRelationships), reportsTo: nullIfBlank(f.reportsTo),
    // The dataset version is stamped by the SERVER when it saves; what is sent back is only what was loaded, and the server ignores it.
    classification: {
      socStatus, socNotYetClassifiedReason: socStatus === 'NotYetClassified' ? nullIfBlank(f.socNotYetClassifiedReason) : null,
      socDatasetVersion: b.classification.socDatasetVersion,
      onetStatus, onetNotYetClassifiedReason: onetStatus === 'NotYetClassified' ? nullIfBlank(f.onetNotYetClassifiedReason) : null,
      onetDatasetVersion: b.classification.onetDatasetVersion,
    },
    qualifications: {
      minimumEducation: nullIfBlank(f.minimumEducation), preferredEducation: nullIfBlank(f.preferredEducation),
      minimumExperience: nullIfBlank(f.minimumExperience), preferredExperience: nullIfBlank(f.preferredExperience), licenses: split(f.licenses),
      certifications: split(f.certifications), technicalSkills: split(f.technicalSkills), professionalSkills: split(f.professionalSkills),
      preferredSkills: split(f.preferredSkills), languages: split(f.languages), travel: nullIfBlank(f.travel), workArrangement: nullIfBlank(f.workArrangement),
      physicalRequirements: nullIfBlank(f.physicalRequirements), workingConditions: nullIfBlank(f.workingConditions),
    },
    employmentGuidance: guidanceFromForm(f, b),
  };
}

/** `hourlyOrSalaried` is an optional member of the wire document: it is sent only when there is a value, and a cleared one is dropped, not nulled. */
function guidanceFromForm(f: RoleForm, b: JobRoleContent): JobRoleContent['employmentGuidance'] {
  const guidance: JobRoleContent['employmentGuidance'] = {
    ...b.employmentGuidance,
    typicalEmploymentType: nullIfBlank(f.typicalEmploymentType), typicalPayType: nullIfBlank(f.typicalPayType),
    flsaClassification: nullIfBlank(f.flsaClassification), commissionEligible: fromTri(f.commissionEligible),
    bonusEligible: fromTri(f.bonusEligible), overtimeEligible: fromTri(f.overtimeEligible),
    benefitEligibilityGuidance: nullIfBlank(f.benefitEligibilityGuidance),
  };
  const hourly = nullIfBlank(f.hourlyOrSalaried);
  if (hourly) guidance.hourlyOrSalaried = hourly;
  else delete guidance.hourlyOrSalaried;
  return guidance;
}

export function headerFromForm(f: RoleForm): JobRoleHeaderInput {
  return {
    title: f.title.trim(), description: nullIfBlank(f.description), roleCode: nullIfBlank(f.roleCode), departmentId: f.departmentId || null, jobFamily: nullIfBlank(f.jobFamily),
    careerLevel: nullIfBlank(f.careerLevel), managementLevel: nullIfBlank(f.managementLevel), socCode: nullIfBlank(f.socCode),
    onetCode: nullIfBlank(f.onetCode),
  };
}

export interface FormIssue {
  key: RoleFormKey;
  message: string;
}

/** The characters a role code may contain (the server answers 400 otherwise, also when only checking availability). */
export const ROLE_CODE_FORMAT = /^[A-Za-z0-9][A-Za-z0-9._-]*$/;

/** A role needs a title to be saved even as a Draft. */
export function validateForDraft(f: RoleForm): FormIssue[] {
  const issues: FormIssue[] = [];
  if (!f.title.trim()) issues.push({ key: 'title', message: 'Enter a role title.' });
  else if (f.title.trim().length > 150) issues.push({ key: 'title', message: 'The role title must be 150 characters or fewer.' });
  if (f.description.trim().length > 500) issues.push({ key: 'description', message: 'The description must be 500 characters or fewer.' });
  if (f.roleCode.trim().length > 40) issues.push({ key: 'roleCode', message: 'The internal code must be 40 characters or fewer.' });
  else if (f.roleCode.trim() && !ROLE_CODE_FORMAT.test(f.roleCode.trim())) issues.push({ key: 'roleCode', message: 'The internal code may contain only letters, numbers, dots, dashes and underscores.' });
  if (f.socCode.trim() && !/^\d{2}-\d{4}(\.\d{2})?$/.test(f.socCode.trim())) {
    issues.push({ key: 'socCode', message: 'Enter the SOC code like 43-3051, or leave it blank.' });
  }
  if (f.budgetAuthority.trim().length > 500) issues.push({ key: 'budgetAuthority', message: 'The budget authority text must be 500 characters or fewer.' });
  if (f.socStatus === 'NotYetClassified' && !f.socNotYetClassifiedReason.trim()) {
    issues.push({ key: 'socNotYetClassifiedReason', message: 'Say why the SOC code is not yet classified, or choose a code.' });
  }
  if (f.socNotYetClassifiedReason.trim().length > 300) issues.push({ key: 'socNotYetClassifiedReason', message: 'The reason must be 300 characters or fewer.' });
  if (f.onetStatus === 'NotYetClassified' && !f.onetNotYetClassifiedReason.trim()) {
    issues.push({ key: 'onetNotYetClassifiedReason', message: 'Say why the O*NET code is not yet classified, or choose a code.' });
  }
  if (f.onetNotYetClassifiedReason.trim().length > 300) issues.push({ key: 'onetNotYetClassifiedReason', message: 'The reason must be 300 characters or fewer.' });
  return issues;
}

/** What the screen asks for before offering to publish. The server still decides: its refusal is shown as it comes. */
export function validateForActivation(f: RoleForm): FormIssue[] {
  const issues = validateForDraft(f);
  if (!f.summary.trim()) issues.push({ key: 'summary', message: 'Add a short summary of the role before activating it.' });
  if (split(f.essentialDuties).length === 0) issues.push({ key: 'essentialDuties', message: 'Add at least one essential duty before activating the role.' });
  return issues;
}
